/**
 * Axios request interceptor that acquires a fresh access token from MSAL on
 * every request. MSAL handles its own caching and silent refresh, so this
 * gives us:
 *   - a fresh, never-stale token on the wire,
 *   - no manual token storage in sessionStorage,
 *   - automatic recovery from expiry without a hard page reload.
 *
 * The interceptor is the single source of truth for the Authorization
 * header. It always overwrites whatever the caller passed.
 */
import axios from "axios";
import { getMsalInstance } from "./msalClient";
import { getMsalAccount } from "./msalAccount";
import { getBackendScopes } from "./authConfig";
import { isLikelyJwt } from "./tokenShape";

let interceptorId = null;

export const setupMsalAxiosInterceptor = () => {
  if (interceptorId !== null) return;

  interceptorId = axios.interceptors.request.use(
    async (config) => {
      const instance = getMsalInstance();
      const account = getMsalAccount();

      // Not in MSAL mode (e.g. tests, or pre-bootstrap) — let the caller's
      // headers stand untouched.
      if (!instance || !account) return config;

      try {
        const response = await instance.acquireTokenSilent({
          scopes: getBackendScopes(),
          account,
        });
        const accessToken = response?.accessToken;
        if (isLikelyJwt(accessToken)) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${accessToken}`;
        } else if (config.headers) {
          // Guard against "Bearer null"/"Bearer undefined" leaking through.
          delete config.headers.Authorization;
        }
      } catch {
        // Silent acquisition failed (interaction required, bad config,
        // network down, etc.). Strip any stale Authorization header so the
        // backend's 401 carries a clean "no auth" signal and the existing
        // re-auth redirect can fire.
        if (config.headers) delete config.headers.Authorization;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );
};

export const removeMsalAxiosInterceptor = () => {
  if (interceptorId !== null) {
    axios.interceptors.request.eject(interceptorId);
    interceptorId = null;
  }
};
